import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { 
  ThemePreset, 
  ThemeTokens, 
  THEME_PRESETS, 
  getPresetById, 
  mergeTokens, 
  TOKEN_TO_CSS_VAR 
} from '@/themes/presets';
import { useAdminDealershipContextSafe } from '@/context/AdminDealershipContext';
import { useDealership } from '@/hooks/useDealership';
import { toast } from 'sonner';
import { captureError } from '@/lib/sentry';

type DarkModePreference = 'light' | 'dark' | 'auto';

interface ThemeContextValue {
  // Current state
  currentPreset: ThemePreset;
  presets: ThemePreset[];
  overrides: Partial<ThemeTokens>;
  darkMode: DarkModePreference;
  resolvedMode: 'light' | 'dark';
  highlightColor: string;
  
  // Actions
  setPreset: (id: string) => Promise<void>;
  setOverrides: (overrides: Partial<ThemeTokens>) => Promise<void>;
  setDarkMode: (mode: DarkModePreference) => Promise<void>;
  previewPreset: (id: string) => void;
  previewOverrides: (overrides: Partial<ThemeTokens>) => void;
  resetToPreset: () => void;
  saveTheme: () => Promise<void>;
  
  // State flags
  hasUnsavedChanges: boolean;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const hexToHSL = (hex: string): string => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.substring(1, 3), 16);
    g = parseInt(hex.substring(3, 5), 16);
    b = parseInt(hex.substring(5, 7), 16);
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

// Safe hook that returns null if not in provider (for components that may render outside)
export const useThemeSafe = () => {
  return useContext(ThemeContext);
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

interface PendingDarkModeSync {
  requestedMode: DarkModePreference;
  ignoredStaleResponse: boolean;
  requestId: number;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const adminContext = useAdminDealershipContextSafe();
  const { dealership: userDealership, loading: userDealershipLoading, updateDealership } = useDealership();
  
  // Use admin context dealership if available and admin is impersonating, otherwise use user's dealership
  const dealership = adminContext?.activeDealership || userDealership;
  const dealershipLoading = adminContext?.loading || userDealershipLoading;
  
  // State for current theme configuration
  const [presetId, setPresetId] = useState<string>('default');
  const [overrides, setOverrides] = useState<Partial<ThemeTokens>>({});
  const [darkMode, setDarkModeState] = useState<DarkModePreference>('auto');
  const [resolvedMode, setResolvedMode] = useState<'light' | 'dark'>('light');
  
  // Preview state (for instant feedback before saving)
  const [previewPresetId, setPreviewPresetId] = useState<string | null>(null);
  const [previewOverridesState, setPreviewOverridesState] = useState<Partial<ThemeTokens> | null>(null);
  
  const [isLoading, setIsLoading] = useState(true);
  const syncedDealershipIdRef = useRef<string | null | undefined>(undefined);
  const pendingDarkModeSyncRef = useRef<PendingDarkModeSync | null>(null);
  const darkModeRequestIdRef = useRef(0);
  
  // Compute the current preset
  const currentPreset = useMemo(() => {
    const id = previewPresetId ?? presetId;
    return getPresetById(id);
  }, [previewPresetId, presetId]);
  
  // Compute effective overrides
  const effectiveOverrides = useMemo(() => {
    return previewOverridesState ?? overrides;
  }, [previewOverridesState, overrides]);
  
  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return previewPresetId !== null || previewOverridesState !== null;
  }, [previewPresetId, previewOverridesState]);
  
  // Compute highlight color for charts
  const highlightColor = useMemo(() => {
    const tokens = mergeTokens(currentPreset, effectiveOverrides, resolvedMode);
    return tokens.brand;
  }, [currentPreset, effectiveOverrides, resolvedMode]);
  
  // Resolve dark mode based on preference and system setting
  useEffect(() => {
    const resolveMode = () => {
      if (darkMode === 'auto') {
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setResolvedMode(prefersDark ? 'dark' : 'light');
      } else {
        setResolvedMode(darkMode);
      }
    };
    
    resolveMode();
    
    // Listen for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      if (darkMode === 'auto') {
        resolveMode();
      }
    };
    
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [darkMode]);
  
  // Apply theme tokens to CSS variables
  useEffect(() => {
    const tokens = mergeTokens(currentPreset, effectiveOverrides, resolvedMode);
    const root = document.documentElement;
    
    // Apply each token as a CSS variable
    Object.entries(TOKEN_TO_CSS_VAR).forEach(([tokenKey, cssVar]) => {
      const value = tokens[tokenKey as keyof ThemeTokens];
      if (value) {
        if (value.startsWith('#')) {
          root.style.setProperty(cssVar, hexToHSL(value));
        } else {
          root.style.setProperty(cssVar, value);
        }
      }
    });
    
    // Toggle dark class
    root.classList.toggle('dark', resolvedMode === 'dark');
  }, [currentPreset, effectiveOverrides, resolvedMode]);
  
  // Load theme from dealership on mount
  useEffect(() => {
    if (dealershipLoading) return;

    const currentDealershipId = dealership?.id ?? null;
    const dealershipChanged = syncedDealershipIdRef.current !== currentDealershipId;
    syncedDealershipIdRef.current = currentDealershipId;
    
    if (dealership) {
      setPresetId(dealership.theme_preset || 'default');
      setOverrides((dealership.theme_overrides as Partial<ThemeTokens>) || {});
      const serverDarkMode = (dealership.dark_mode_preference as DarkModePreference) || 'auto';
      const pendingDarkModeSync = pendingDarkModeSyncRef.current;

      if (!pendingDarkModeSync || dealershipChanged) {
        pendingDarkModeSyncRef.current = null;
        setDarkModeState(serverDarkMode);
      } else if (serverDarkMode === pendingDarkModeSync.requestedMode) {
        pendingDarkModeSyncRef.current = null;
        setDarkModeState(serverDarkMode);
      } else if (!pendingDarkModeSync.ignoredStaleResponse) {
        pendingDarkModeSyncRef.current = {
          ...pendingDarkModeSync,
          ignoredStaleResponse: true,
        };
      } else {
        pendingDarkModeSyncRef.current = null;
        setDarkModeState(serverDarkMode);
      }
    } else {
      pendingDarkModeSyncRef.current = null;
      // No dealership - use defaults and check localStorage for dark mode
      const savedDarkMode = localStorage.getItem('theme') as DarkModePreference | null;
      if (savedDarkMode && ['light', 'dark', 'auto'].includes(savedDarkMode)) {
        setDarkModeState(savedDarkMode);
      }
    }
    
    setIsLoading(false);
  }, [dealership, dealershipLoading]);
  
  // Preview a preset without saving
  const previewPreset = useCallback((id: string) => {
    setPreviewPresetId(id);
  }, []);
  
  // Preview overrides without saving
  const previewOverrides = useCallback((newOverrides: Partial<ThemeTokens>) => {
    setPreviewOverridesState(prev => ({
      ...(prev ?? overrides),
      ...newOverrides,
    }));
  }, [overrides]);
  
  // Reset preview to saved state
  const resetToPreset = useCallback(() => {
    setPreviewPresetId(null);
    setPreviewOverridesState(null);
  }, []);
  
  // Save theme to dealership
  const saveTheme = useCallback(async () => {
    const newPresetId = previewPresetId ?? presetId;
    const newOverrides = previewOverridesState ?? overrides;
    
    if (dealership) {
      await updateDealership({
        theme_preset: newPresetId,
        theme_overrides: newOverrides as Record<string, unknown>,
        dark_mode_preference: darkMode,
      });
    }
    
    // Update local state
    setPresetId(newPresetId);
    setOverrides(newOverrides);
    
    // Clear preview state
    setPreviewPresetId(null);
    setPreviewOverridesState(null);
  }, [dealership, updateDealership, previewPresetId, presetId, previewOverridesState, overrides, darkMode]);
  
  // Set preset and save
  const setPreset = useCallback(async (id: string) => {
    if (dealership) {
      await updateDealership({
        theme_preset: id,
        theme_overrides: {},
      });
      setPresetId(id);
      setOverrides({});
      setPreviewPresetId(null);
      setPreviewOverridesState(null);
    } else {
      setPresetId(id);
      setOverrides({});
    }
  }, [dealership, updateDealership]);
  
  // Set overrides and save
  const setOverridesAndSave = useCallback(async (newOverrides: Partial<ThemeTokens>) => {
    const merged = { ...overrides, ...newOverrides };
    
    if (dealership) {
      await updateDealership({
        theme_overrides: merged as Record<string, unknown>,
      });
    }
    
    setOverrides(merged);
    setPreviewOverridesState(null);
  }, [dealership, updateDealership, overrides]);
  
  // Set dark mode preference
  const setDarkMode = useCallback(async (mode: DarkModePreference) => {
    const requestId = darkModeRequestIdRef.current + 1;
    darkModeRequestIdRef.current = requestId;
    const previousDarkMode = darkMode;
    setDarkModeState(mode);
    
    // Always save to localStorage for non-dealership users
    localStorage.setItem('theme', mode);
    
    if (dealership) {
      pendingDarkModeSyncRef.current = {
        requestedMode: mode,
        ignoredStaleResponse: false,
        requestId,
      };

      try {
        await updateDealership({
          dark_mode_preference: mode,
        });
      } catch (error) {
        if (darkModeRequestIdRef.current !== requestId) {
          return;
        }

        pendingDarkModeSyncRef.current = null;
        setDarkModeState(previousDarkMode);
        localStorage.setItem('theme', previousDarkMode);
        captureError(error instanceof Error ? error : String(error), {
          feature: 'theme',
          action: 'setDarkMode',
          requested_mode: mode,
          previous_mode: previousDarkMode,
          dealership_id: dealership.id,
        });
        toast.error("Couldn't save theme preference");
      }
    }
  }, [darkMode, dealership, updateDealership]);
  
  const value: ThemeContextValue = {
    currentPreset,
    presets: THEME_PRESETS,
    overrides: effectiveOverrides,
    darkMode,
    resolvedMode,
    highlightColor,
    setPreset,
    setOverrides: setOverridesAndSave,
    setDarkMode,
    previewPreset,
    previewOverrides,
    resetToPreset,
    saveTheme,
    hasUnsavedChanges,
    isLoading: isLoading || dealershipLoading,
  };
  
  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
